/**
 * ONE place that turns a failure into words a person can act on.
 *
 * Two shapes come through the UI:
 *  - a thrown ApiError (Auth/C2API: { status, statusText, body, message }) — `apiErrorText`;
 *  - a job's recorded failure (errorCode · upstreamHttpStatus · upstreamErrorBody, or the raw
 *    `errorMessage` a worker wrote) — `explainJobError`, which gives a plain-language headline, a
 *    next step, and the full raw detail for a details panel. Nothing here truncates the detail:
 *    a clamped Java exception on a card is what this module exists to replace.
 */

/** The server's own sentence for a failed call, else the HTTP status, else the thrown message. */
export const apiErrorText = (e, fallback = 'Unexpected error.') => {
  if (!e) return fallback;
  const body = e.body;
  if (body && typeof body === 'object') {
    if (body.message) return String(body.message);
    if (body.error) return String(body.error);
  }
  // axios-style errors (not produced by C2API, but some callers still catch them)
  if (e.response && e.response.data && e.response.data.message) return String(e.response.data.message);
  if (e.status) return `Server returned ${e.status}${e.statusText ? ' ' + e.statusText : ''}.`;
  if (e.message) return String(e.message);
  return typeof e === 'string' && e ? e : fallback;
};

/** message / error / detail out of a JSON upstream body; plain text as-is; '' when empty. */
export const parseUpstreamBody = (body) => {
  if (body == null) return '';
  const raw = typeof body === 'string' ? body.trim() : '';
  if (typeof body === 'object') {
    const d = body.message || body.error || body.detail;
    return d ? String(d).trim() : '';
  }
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    const d = parsed && (parsed.message || parsed.error || parsed.detail);
    return d ? String(d).trim() : '';
  } catch (err) {
    return raw;
  }
};

// Ordered: the first rule whose test matches the raw text or the error code wins. Keep the
// headlines short and the next steps concrete — they are the only thing most users will read.
const RULES = [
  {
    test: /PoolInitializationException|Failed to initialize pool|Connection refused|UnknownHostException|Communications link failure|could not connect|connect timed out|No route to host/i,
    headline: 'Could not connect to the database.',
    nextStep: 'Check the host, port and credentials under Settings → Data sources, then run Test connection.',
  },
  {
    test: /'encrypt' property|property 'encrypt'|encrypt.*(not supported|invalid|unrecognized)/i,
    headline: "The JDBC driver rejected the connection property 'encrypt'.",
    nextStep: "Edit the data source's advanced connection properties and remove or correct 'encrypt'.",
  },
  {
    test: /Access denied|authentication failed|Login failed|password authentication|invalid credentials|401/i,
    headline: 'The database refused the login.',
    nextStep: 'Re-enter the username and password for this data source and test the connection.',
  },
  {
    test: /permission denied|insufficient privilege|not authorized|403/i,
    headline: 'The database account lacks a required permission.',
    nextStep: 'Grant the account read (and, for masking, write) access to the affected schema.',
  },
  {
    test: /ENGINE_NOT_MASKABLE/i,
    headline: 'This engine cannot be masked yet.',
    nextStep: 'Pick a target the masking service supports, or run discovery only.',
  },
  {
    test: /WORKER_REJECTED|UNPROCESSABLE_ENTITY|422/i,
    headline: 'The worker refused the job before it started.',
    nextStep: 'Open the job results for the rejection reason; it usually names the table or setting involved.',
  },
  {
    test: /LICENSE/i,
    headline: 'The license does not allow this job.',
    nextStep: 'Check the license under Admin Panel → License.',
  },
  {
    test: /WORKER_UNAVAILABLE|503|Service Unavailable|no worker/i,
    headline: 'The worker service was not reachable.',
    nextStep: 'Check service health under Advanced Config → Health, then re-run the job.',
  },
  {
    test: /timeout|timed out/i,
    headline: 'The job timed out.',
    nextStep: 'Re-run the job; if it keeps timing out, lower the sampling rate or narrow the scope.',
  },
  {
    test: /OutOfMemory|out of memory|heap space/i,
    headline: 'The worker ran out of memory.',
    nextStep: 'Re-run with a smaller scope, or raise the worker memory limit.',
  },
  {
    test: /CANCELLED|cancelled by/i,
    headline: 'The job was cancelled.',
    nextStep: 'Re-run it when ready.',
  },
];

const DEFAULT = {
  headline: 'The job failed.',
  nextStep: 'Open the details for the message the service returned, then re-run once the cause is fixed.',
};

/**
 * Plain-language explanation of a recorded job failure.
 * @param {string|object|null} raw   the worker's errorMessage / upstreamErrorBody (text or JSON)
 * @param {{errorCode?:string, upstreamHttpStatus?:number|string}} meta
 * @returns {{ headline:string, nextStep:string, detail:string, code:string, httpStatus:string, matched:boolean }}
 */
export const explainJobError = (raw, meta = {}) => {
  const code = meta.errorCode ? String(meta.errorCode).trim() : '';
  const httpStatus = meta.upstreamHttpStatus != null ? String(meta.upstreamHttpStatus) : '';
  const detail = typeof raw === 'object' && raw != null ? JSON.stringify(raw, null, 2) : String(raw || '').trim();
  const parsed = parseUpstreamBody(raw);
  const haystack = `${code} ${httpStatus} ${parsed} ${detail}`;
  const rule = RULES.find((r) => r.test.test(haystack));
  return {
    headline: rule ? rule.headline : DEFAULT.headline,
    nextStep: rule ? rule.nextStep : DEFAULT.nextStep,
    detail: detail || parsed,
    code,
    httpStatus,
    matched: Boolean(rule),
  };
};

/**
 * One-line summary of a job's failure for tooltips and ledgers: `CODE · HTTP nnn: message`.
 * (Was Secure/api.readableJobError, which still delegates here.) Caps a non-JSON body at 300
 * characters so a tooltip stays usable — callers wanting the whole text use explainJobError.
 */
export const readableJobErrorText = (job) => {
  if (!job) return '';
  const parts = [];
  const code = job.errorCode ? String(job.errorCode).trim() : '';
  const httpStatus = job.upstreamHttpStatus != null ? String(job.upstreamHttpStatus) : '';
  let detail = parseUpstreamBody(job.upstreamErrorBody);
  if (detail.length > 300) detail = `${detail.slice(0, 300)}…`;
  if (code) parts.push(code);
  if (httpStatus) parts.push(`HTTP ${httpStatus}`);
  const prefix = parts.join(' · ');
  if (prefix && detail) return `${prefix}: ${detail}`;
  if (detail) return detail;
  if (prefix) return prefix;
  return 'Job failed; no error details were returned.';
};
